import { useMutation, useQueryClient } from '@tanstack/react-query'
import { enqueueSnackbar } from 'notistack'

import { addProductsToFavoriteList } from '@/gen/bff'
import { getAllFavoriteListIdsByProductKeys } from '@/hooks/bff/favorite/getAllFavoriteListIdsByProducts/queryKeys'
import { getFavoriteListFullKeys } from '@/hooks/bff/favorite/getFavoriteListFull/queryKeys'
import { useOrgScopeSnapshot } from '@/hooks/useOrgScopeSnapshot'
import { useTranslations } from '@/hooks/useTranslations'

type AddProductPayload = {
  favoriteListId: string
  productIds: string[]
}

export const useAddProductToFavoriteList = () => {
  const queryClient = useQueryClient()
  const { translate } = useTranslations()
  const snapshotOrgScope = useOrgScopeSnapshot()

  return useMutation({
    mutationFn: ({ favoriteListId, productIds }: AddProductPayload) =>
      addProductsToFavoriteList({
        path: { favoriteListId },
        body: { productIds },
      }),

    onMutate: snapshotOrgScope,

    onSuccess: (_data, { productIds }, context) => {
      queryClient.invalidateQueries({
        queryKey: getFavoriteListFullKeys.all,
      })
      // Per-product invalidation avoids refetching every cached product across every organisation visited this session.
      productIds.forEach((productId) => {
        queryClient.invalidateQueries({
          queryKey: getAllFavoriteListIdsByProductKeys.product(context.customerId, context.orgUnitId, productId),
        })
      })

      enqueueSnackbar(translate('favoriteList.addProduct'), {
        variant: 'success',
      })
    },

    onError: (error) => {
      enqueueSnackbar(error?.message || translate('common.api_error'), { variant: 'error' })
    },

    meta: {
      errorMessage: translate('common.api_error'),
    },
  })
}
